<template>
  <div class="prospect-stats-strip">
    <div class="prospect-stat">
      <span class="prospect-stat-label">Active trials</span>
      <span class="prospect-stat-value">{{ summary.active_trials_count || 0 }}</span>
    </div>
    <div class="prospect-stat" style="position: relative; justify-content: center; min-width: 160px;">
      <span class="prospect-stat-label">
        Conversion rate
        <VDropdown theme="info-tooltip">
          <i class="fa-solid fa-circle-info text-gray-400 m-l-xxs" style="cursor: help;"></i>
          <template v-slot:popper>
            <div>
              <p class="header">How is this calculated?</p>
              <p>
                Conversions in the selected window divided by inquiries created in the same window.
              </p>
              <p>
                A conversion is counted when a prospect's stage changes to Converted; an inquiry
                is counted on the day the lead was created.
              </p>
              <p>
                The numerator and denominator are counted independently, so the rate can
                exceed 100% in a window where you converted more old leads than you took in
                new ones.
              </p>
            </div>
          </template>
        </VDropdown>
      </span>
      <a href="javascript:void(0)" class="text-14" @click="togglePopover">
        {{ summary.conversion_rate !== undefined ? summary.conversion_rate + '%' : '—' }}
        <span v-if="hasCountBreakdown" class="text-12 text-gray-500">
          · {{ summary.conversion_count }} / {{ summary.conversion_inquiry_count }}
        </span>
        <span class="text-12 text-gray-500"> · {{ rangeLabel }}</span>
        <i class="fa-solid fa-caret-down m-l-xxs"></i>
      </a>
      <div v-if="showPopover" class="log-contact-popover" @click.stop>
        <h5 class="m-t-none m-b-sm font-semi">Conversion window</h5>
        <div class="form-group m-b-sm">
          <select v-model="localRange" class="form-control input-sm" @change="onRangeChange">
            <option v-for="opt in rangeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div v-if="localRange === 'custom'" class="form-group m-b-sm flex gap-x-xs align-items-center flex-wrap">
          <input v-model="localStart" type="date" class="form-control input-sm" style="width: auto;" />
          <span class="text-12 text-gray-500">to</span>
          <input v-model="localEnd" type="date" class="form-control input-sm" style="width: auto;" />
        </div>
        <div class="flex justify-space-between">
          <button type="button" class="btn btn-default btn-sm" @click="showPopover = false">Close</button>
          <button
            v-if="localRange === 'custom'"
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!localStart || !localEnd"
            @click="applyCustom"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'prospect-stats-strip',
  props: {
    summary: { type: Object, default: () => ({}) },
    conversionRange: { type: String, default: '90' },
    conversionStart: { type: String, default: '' },
    conversionEnd: { type: String, default: '' },
  },
  data() {
    return {
      showPopover: false,
      localRange: this.conversionRange,
      localStart: this.conversionStart,
      localEnd: this.conversionEnd,
    };
  },
  computed: {
    rangeOptions() {
      return [
        { label: '30-Day', value: '30' },
        { label: '60-Day', value: '60' },
        { label: '90-Day', value: '90' },
        { label: '180-Day', value: '180' },
        { label: 'Custom', value: 'custom' },
      ];
    },
    hasCountBreakdown() {
      return this.summary.conversion_count !== undefined
        && this.summary.conversion_inquiry_count !== undefined;
    },
    rangeLabel() {
      if (this.conversionRange === 'custom') {
        if (this.conversionStart && this.conversionEnd) {
          return `${this.formatDate(this.conversionStart)} – ${this.formatDate(this.conversionEnd)}`;
        }
        return 'Custom';
      }
      const match = this.rangeOptions.find((o) => o.value === this.conversionRange);
      return match ? match.label : '';
    },
  },
  watch: {
    conversionRange(v) { this.localRange = v; },
    conversionStart(v) { this.localStart = v; },
    conversionEnd(v) { this.localEnd = v; },
  },
  methods: {
    togglePopover() {
      this.showPopover = !this.showPopover;
    },
    onRangeChange() {
      if (this.localRange !== 'custom') {
        this.$emit('range-change', { range: this.localRange });
      }
    },
    applyCustom() {
      if (!this.localStart || !this.localEnd) return;
      this.$emit('range-change', { range: 'custom', start: this.localStart, end: this.localEnd });
      this.showPopover = false;
    },
    // Parse YYYY-MM-DD as local (not UTC) so we don't lose a day to TZ.
    formatDate(isoDateString) {
      if (!isoDateString) return '';
      const [y, m, d] = isoDateString.split('-').map(Number);
      if (!y || !m || !d) return isoDateString;
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
  },
};
</script>
